import {
    Eye,
    EyeOff,
    FileImage,
    Image as ImageIcon,
    Link2,
    Pause,
    Trash2,
    Upload,
    UserRoundX,
    Video,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { ResultOverlay } from './ResultOverlay';
import { ApplicationError } from '../domain/errors';
import { getMediaSourceUrl, inferMediaKind, normalizeMediaUrl } from '../domain/media';
import { translateError } from '../i18n/errors';
import { useTranslate } from '../i18n/useI18n';

import type { OutputSettings } from '../domain/analysis';
import type { MediaKind, MediaRole, MediaSource } from '../domain/media';
import type { FaceResult } from '../lib/faceAnalysis';
import type { ReactElement } from 'react';

function createFileSource(file: File): MediaSource {
    const kind = inferMediaKind(file);
    if (kind === undefined) throw new ApplicationError('invalidFile');
    return {
        id: crypto.randomUUID(),
        origin: 'file',
        kind,
        name: file.name,
        file,
        previewUrl: URL.createObjectURL(file),
    };
}

export function MediaInput({
    role,
    source,
    disabled,
    faces,
    labels,
    output,
    matching,
    onSourceChange,
    onReadyChange,
    onMediaElement,
    onFrameChange,
    onOutputChange,
}: {
    role: MediaRole;
    source: MediaSource | null;
    disabled: boolean;
    faces: FaceResult[];
    labels: string[];
    output: OutputSettings;
    matching: boolean;
    onSourceChange: (source: MediaSource | null) => void;
    onReadyChange: (ready: boolean) => void;
    onMediaElement: (element: HTMLImageElement | HTMLVideoElement | null) => void;
    onFrameChange: () => void;
    onOutputChange: (output: OutputSettings) => void;
}): ReactElement {
    const translate = useTranslate();
    const inputId = useId();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [urlOpen, setUrlOpen] = useState(false);
    const [urlValue, setUrlValue] = useState('');
    const [urlKind, setUrlKind] = useState<MediaKind>('image');
    const [error, setError] = useState<unknown>();
    const [dragging, setDragging] = useState(false);
    const [mediaElement, setMediaElement] = useState<HTMLImageElement | HTMLVideoElement | null>(
        null
    );
    const mediaUrl = source !== null ? getMediaSourceUrl(source) : '';
    const isReference = role === 'reference';
    const drawOverlay = output.drawOverlay[role];

    useEffect(() => {
        onMediaElement(mediaElement);
        return () => {
            onMediaElement(null);
        };
    }, [mediaElement, onMediaElement]);

    const acceptFile = (file?: File): void => {
        if (file === undefined) return;
        try {
            onSourceChange(createFileSource(file));
            setUrlOpen(false);
            setError(undefined);
        } catch (caught) {
            setError(
                caught instanceof ApplicationError ? caught : new ApplicationError('fileReadFailed')
            );
        }
    };
    const acceptUrl = (): void => {
        try {
            const url = normalizeMediaUrl(urlValue);
            onSourceChange({
                id: crypto.randomUUID(),
                origin: 'url',
                kind: urlKind,
                name: new URL(url).hostname,
                url,
            });
            setUrlOpen(false);
            setError(undefined);
        } catch (caught) {
            setError(
                caught instanceof ApplicationError ? caught : new ApplicationError('invalidUrl')
            );
        }
    };

    return (
        <section className={`media-card${disabled ? ' media-card--disabled' : ''}`}>
            <div className='media-card__header'>
                <span className='eyebrow'>
                    {translate(isReference ? 'media.referenceTitle' : 'media.targetTitle')}
                </span>
                <span className={`status-dot${source !== null ? ' status-dot--ready' : ''}`}>
                    {source !== null
                        ? source.name
                        : translate(disabled ? 'media.notRequired' : 'media.waiting')}
                </span>
            </div>
            {source === null ? (
                <div
                    className={`drop-zone${dragging ? ' drop-zone--dragging' : ''}`}
                    role='group'
                    aria-label={translate(isReference ? 'media.referenceArea' : 'media.targetArea')}
                    onDragEnter={(event) => {
                        event.preventDefault();
                        if (!disabled) setDragging(true);
                    }}
                    onDragOver={(event) => {
                        event.preventDefault();
                    }}
                    onDragLeave={() => {
                        setDragging(false);
                    }}
                    onDrop={(event) => {
                        event.preventDefault();
                        setDragging(false);
                        if (!disabled) acceptFile(event.dataTransfer.files[0]);
                    }}
                >
                    <div className='drop-zone__glyph'>
                        {isReference ? (
                            <ImageIcon aria-hidden='true' />
                        ) : (
                            <FileImage aria-hidden='true' />
                        )}
                    </div>
                    <strong>
                        {translate(isReference ? 'media.addReference' : 'media.addTarget')}
                    </strong>
                    <p>
                        {disabled ? translate('media.referenceDisabled') : translate('media.drop')}
                    </p>
                    <div className='source-actions'>
                        <button
                            type='button'
                            disabled={disabled}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Upload aria-hidden='true' /> {translate('media.chooseFile')}
                        </button>
                        <button
                            type='button'
                            className='button-secondary'
                            disabled={disabled}
                            onClick={() => {
                                setUrlOpen((open) => !open);
                            }}
                        >
                            <Link2 aria-hidden='true' /> {translate('media.addUrl')}
                        </button>
                    </div>
                    <input
                        ref={fileInputRef}
                        id={inputId}
                        className='visually-hidden'
                        type='file'
                        accept='image/*,video/*'
                        disabled={disabled}
                        onChange={(event) => {
                            acceptFile(event.target.files?.[0]);
                            event.target.value = '';
                        }}
                    />
                    {urlOpen && (
                        <div className='url-form'>
                            <div
                                className='segmented'
                                aria-label={translate('media.urlType')}
                            >
                                <button
                                    type='button'
                                    className={urlKind === 'image' ? 'is-active' : ''}
                                    aria-pressed={urlKind === 'image'}
                                    onClick={() => {
                                        setUrlKind('image');
                                    }}
                                >
                                    <ImageIcon aria-hidden='true' /> {translate('media.image')}
                                </button>
                                <button
                                    type='button'
                                    className={urlKind === 'video' ? 'is-active' : ''}
                                    aria-pressed={urlKind === 'video'}
                                    onClick={() => {
                                        setUrlKind('video');
                                    }}
                                >
                                    <Video aria-hidden='true' /> {translate('media.video')}
                                </button>
                            </div>
                            <label>
                                <span className='visually-hidden'>
                                    {translate('media.urlAddress')}
                                </span>
                                <input
                                    type='url'
                                    value={urlValue}
                                    placeholder={translate('media.urlPlaceholder')}
                                    onChange={(event) => {
                                        setUrlValue(event.target.value);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') acceptUrl();
                                    }}
                                />
                            </label>
                            <button
                                type='button'
                                onClick={acceptUrl}
                                disabled={urlValue.trim().length === 0}
                            >
                                {translate('media.load')}
                            </button>
                            <small>{translate('media.cors')}</small>
                        </div>
                    )}
                    {error !== undefined && (
                        <p
                            className='inline-error'
                            role='alert'
                        >
                            {translateError(error, translate, 'error.fileRead')}
                        </p>
                    )}
                </div>
            ) : (
                <div className='media-preview'>
                    <div className='media-preview__toolbar'>
                        <span>
                            {source.kind === 'video' ? (
                                <Video aria-hidden='true' />
                            ) : (
                                <ImageIcon aria-hidden='true' />
                            )}
                            {translate(
                                source.origin === 'file' ? 'media.localFile' : 'media.remoteUrl'
                            )}
                        </span>
                        <div className='media-preview__actions'>
                            <button
                                type='button'
                                disabled={disabled}
                                aria-pressed={drawOverlay}
                                title={translate(
                                    drawOverlay ? 'media.hideLayers' : 'media.showLayers'
                                )}
                                onClick={() => {
                                    onOutputChange({
                                        ...output,
                                        drawOverlay: {
                                            ...output.drawOverlay,
                                            [role]: !drawOverlay,
                                        },
                                    });
                                }}
                            >
                                {drawOverlay ? (
                                    <Eye aria-hidden='true' />
                                ) : (
                                    <EyeOff aria-hidden='true' />
                                )}{' '}
                                {translate('media.layers')}
                            </button>
                            {!isReference && (
                                <button
                                    type='button'
                                    disabled={disabled || !matching || !drawOverlay}
                                    aria-pressed={output.drawUnknownFaces}
                                    title={translate(
                                        output.drawUnknownFaces
                                            ? 'media.hideUnknownFaces'
                                            : 'media.showUnknownFaces'
                                    )}
                                    onClick={() => {
                                        onOutputChange({
                                            ...output,
                                            drawUnknownFaces: !output.drawUnknownFaces,
                                        });
                                    }}
                                >
                                    <UserRoundX aria-hidden='true' />{' '}
                                    {translate('media.unknownFaces')}
                                </button>
                            )}
                            <button
                                type='button'
                                disabled={disabled}
                                onClick={() => {
                                    setError(undefined);
                                    onSourceChange(null);
                                }}
                                aria-label={translate('media.remove')}
                            >
                                <Trash2 aria-hidden='true' /> {translate('media.removeButton')}
                            </button>
                        </div>
                    </div>
                    <div className='media-stage'>
                        <div className='media-frame'>
                            {source.kind === 'image' ? (
                                <img
                                    ref={setMediaElement}
                                    src={mediaUrl}
                                    crossOrigin={source.origin === 'url' ? 'anonymous' : undefined}
                                    alt={translate(
                                        isReference ? 'media.referenceAlt' : 'media.targetAlt'
                                    )}
                                    onLoad={() => {
                                        onReadyChange(true);
                                    }}
                                    onError={() => {
                                        onReadyChange(false);
                                        setError(new ApplicationError('imageLoadFailed'));
                                    }}
                                />
                            ) : (
                                <video
                                    ref={setMediaElement}
                                    src={mediaUrl}
                                    crossOrigin={source.origin === 'url' ? 'anonymous' : undefined}
                                    controls
                                    preload='metadata'
                                    onLoadedData={() => {
                                        onReadyChange(true);
                                    }}
                                    onPlay={onFrameChange}
                                    onSeeked={onFrameChange}
                                    onError={() => {
                                        onReadyChange(false);
                                        setError(new ApplicationError('videoLoadFailed'));
                                    }}
                                />
                            )}
                            <ResultOverlay
                                media={mediaElement}
                                faces={faces}
                                role={role}
                                labels={labels}
                                output={output}
                            />
                        </div>
                    </div>
                    {source.kind === 'video' && (
                        <p className='frame-note'>
                            <Pause aria-hidden='true' /> {translate('media.frameNote')}
                        </p>
                    )}
                    {error !== undefined && (
                        <p
                            className='inline-error'
                            role='alert'
                        >
                            {translateError(error, translate, 'error.unexpected')}
                        </p>
                    )}
                </div>
            )}
        </section>
    );
}
